import { useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import {
  MeterReaderType,
  MeterReaderZoneType,
  SubmitMeterReader,
} from "@/types/meter"
import { getZoneDropdown } from "@/lib/api/billing"
import {
  addMeterReader,
  editMeterReader,
  getUserDropDown,
} from "@/lib/api/meter"
import { getAllZones } from "@/lib/api/zone"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { ComboboxSearch } from "@/components/combobox-search"

import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Textarea } from "../ui/textarea"

interface userData {
  id: number
  name: string
}

interface zoneData {
  label: string
  value: string
  id?: number
}

const formSchema = z.object({
  name_id: z.number().min(1, "Please select a reader meter name"),
  zone_id: z.number().min(1, "Please select a zone"),
  description: z.string().min(1, "Please enter a meter reader description"),
})
export default function MeterReaderForm({
  setShowMeterReaderModal,
  editData,
}: {
  setShowMeterReaderModal: React.Dispatch<React.SetStateAction<boolean>>
  editData?: MeterReaderZoneType
}) {
  const queries = useQueries({
    queries: [
      {
        queryKey: ["meter-reader-users"],
        queryFn: getUserDropDown,
      },
      {
        queryKey: ["zones"],
        queryFn: getZoneDropdown,
      },
    ],
  })

  const results = useQueries({
    queries: [
      {
        queryKey: ["zones"],
        queryFn: getZoneDropdown,
      },
    ],
  })

  const [zones] = results.map((result) => result.data || [])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: "",
      name_id: 0,
      zone_id: 0,
    },
  })

  const { toast } = useToast()
  const navigate = useNavigate()
  const meterReaderUsers: Array<{
    value: string
    label: string
    id?: string | number
  }> =
    (queries[0].data as userData[])
      ?.map((item) => ({
        value: item.name,
        label: item.name,
        id: item.id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value), // Ensure unique values
      ) ?? []

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number
      payload: SubmitMeterReader
    }) => {
      if (id) {
        await editMeterReader(payload, id)
      } else {
        await addMeterReader(payload)
      }
    },
    onSuccess: () => {
      toast({
        description: editData
          ? "Meter reader edited successfully."
          : "Meter reader added successfully.",
      })
      queryClient.invalidateQueries({ queryKey: ["meterReaderDetails"] })
      setShowMeterReaderModal(false)
      navigate({ to: "/meter-connection-settings/meter-reader" })
    },
  })

  function submitMeterForm(values: z.infer<typeof formSchema>) {
    const payload: SubmitMeterReader = {
      name_id: values.name_id,
      zone_id: values.zone_id,
      description: values.description,
    }

    mutation.mutate({
      id: editData?.id as number,
      payload: payload,
    })
  }

  useEffect(() => {
    if (editData) {
      form.reset({
        name_id: editData.name_id ?? 0,
        zone_id: editData.zone_id ?? 0,
        description: editData.description ?? "",
      })
    }
  }, [editData, form])

  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4" />

      <Form {...form}>
        <form
          className="mx-auto grid w-full max-w-4xl grid-cols-1 gap-4"
          onSubmit={form.handleSubmit(submitMeterForm)}
        >
          <Card className="my-4 grid-cols-2 gap-4 p-6 md:grid">
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="name_id"
                render={({ field }) => (
                  <FormItem className="col-span-1 flex flex-col space-y-1.5">
                    <label htmlFor="name">
                      Name<span className="text-red-600">*</span>
                    </label>
                    <ComboboxSearch
                      field={field}
                      options={meterReaderUsers}
                      placeholder="Select Meter Reader"
                      idAsValue
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="zone_id"
                render={({ field }) => (
                  <FormItem className="col-span-1 flex flex-col space-y-1.5">
                    <label htmlFor="zone">
                      Zone<span className="text-red-600">*</span>
                    </label>
                    <ComboboxSearch
                      field={field}
                      options={zones}
                      placeholder="Select Zone"
                      idAsValue
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="col-start-1 col-end-3 mb-4 space-y-1.5">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <div>
                    <label htmlFor="description">
                      Description <span className="text-red-600">*</span>
                    </label>
                    <Textarea
                      id="description"
                      className="h-20 w-full"
                      {...field}
                    />
                    <FormMessage />
                  </div>
                )}
              />
            </div>
          </Card>
          <div className="col-span-2 flex w-full justify-end gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowMeterReaderModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            {editData ? (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Save Changes
              </Button>
            ) : (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Add
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
